import React, { Dispatch } from 'react';
import axios from 'ps-components/lib/apiClient';
import ReportCategory from './ReportCategory';
import ReportFields from './ReportFields';
import { Button, Card, Group, LoadingOverlay, Stack, Switch, Tabs, Text, Title, UnstyledButton } from '@mantine/core';
import { ReportInspectorAction, ReportInspectorState } from '../store';
import addAlert from 'ps-components/AlertToast/addAlert';
import { useQuery } from 'react-query';
import { StoredQuery } from './StoredQuery';
import { Detail } from '../Detail';

interface ReportDetailsProps {
  state: ReportInspectorState
  dispatch: Dispatch<ReportInspectorAction>
}

export const ReportDetails: React.FC<ReportDetailsProps> = ({ state, dispatch }) => {
  const { reportsActiveTab, selectedReportPk } = state

  const getReport = async () => {
    try {
      const response = await axios.apiGet(`/api/marketpay-internal/report-inspector/reports?reportPk=${selectedReportPk}`)
      if (!response.data.success || !response.data.report) throw new Error(response.data.message)
      const { report } = response.data
      return report
    } catch (error) {
      console.error(error)
    }
  }

  const { data: report, isLoading, refetch } = useQuery(['getReport', { selectedReportPk }], getReport, { enabled: true })

  const updateMaterializeMetadata = async (metadata: 'materialize' | 'refresh', value: boolean) => {
    try {
      await axios.apiPut('/api/marketpay-internal/report-inspector/materializeMetadata', {
        reportPk: report.report_pk,
        metadata,
        value
      });
    } catch (error) {
      addAlert('Error updating metadata', 'danger')
      console.error(`Error updating materialize metadata: ${error}`);
    } finally {
      refetch()
    }
  };

  const goToReportWriter = (newOrOld: 'new' | 'old') => {
    const url = newOrOld === 'old' ? `/view/o/${report.url}/mp/iframe/app/#reportWriter/${report.report_pk}` : `/view/o/${report.url}/mp/reports/${report.report_pk}`
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const isActiveTab = (tab: string) => tab === reportsActiveTab ? 'bold' : 'normal'

  if (!report || !selectedReportPk) return <></>

  return (
    <Stack>
      <LoadingOverlay visible={isLoading} />
      <Card>
        <UnstyledButton mb='10px' onClick={() => dispatch({ type: 'setSelectedReportPk', payload: undefined })}>
          <Text size='lg' c='blue'>Return to list</Text>
        </UnstyledButton>
        <Title order={4}>{report.report_name}</Title>
      </Card>
      <Tabs variant="outline" value={reportsActiveTab} onChange={payload => dispatch({ type: 'setReportsActiveTab', payload })}>
        <Tabs.List>
          <Tabs.Tab value="details"><Text size='lg' fw={isActiveTab('details')} c='blue'>Details</Text></Tabs.Tab>
          {report.query &&
            <Tabs.Tab value="query"><Text size='lg' fw={isActiveTab('query')} c='blue'>Stored Query</Text></Tabs.Tab>}
          <Tabs.Tab value="category"><Text size='lg' fw={isActiveTab('category')} c='blue'>Category</Text></Tabs.Tab>
          <Tabs.Tab value="reportFields"><Text size='lg' fw={isActiveTab('reportFields')} c='blue'>Fields and Formulas</Text></Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="details" p="xl">
          <Group wrap='nowrap' align='flex-start'>
            <Stack w='50%'>
              <Detail label='Report PK' value={report.report_pk} />
              <Detail label='Old Report PK' value={report.old_report_pk} />
              <Detail label='Template Report PK' value={report.template_report_pk} />
              <Detail label='Old Template Report PK' value={report.old_template_report_pk} />
              <Detail label='Organization' value={report.org_name} />
              <Detail label='URL' value={report.url} />
              <Detail label='Scope' value={report.scope_name} />
              <Detail label='Scope PK' value={report.scope_pk} />
              <Detail label='Created On' value={`${report.created_time} by ${report.creator_first_name} ${report.creator_last_name} ${report.creator_email ? `(${report.creator_email})` : ''}`} />
              <Detail label='Last Modified' value={`${report.last_modified_time} by ${report.last_mod_first_name} ${report.last_mod_last_name} ${report.last_mod_email ? `(${report.last_mod_email})` : ''}`} />
              <Detail label='Lock Report Editing' value={report.lock_report_editing} />
              <Detail label='Description' value={report.report_description} />
              <Detail label='Tags' value={report.tags} code />
              <Detail label='Query Param Map' value={report.query_param_map} code />
              <Detail label='Metadata' value={report.metadata} code />
            </Stack>
            <Group justify='flex-end' align='flex-end' w='50%' gap='sm'>
            { report.metadata?.advanced_reporting === 'true' &&
              <>
                <Switch label='Materialize' labelPosition='left' checked={report.metadata?.materialize === 'true'} onChange={e => updateMaterializeMetadata('materialize', e.target.checked)} />
                <Switch label='Refresh' labelPosition='left' checked={report.metadata?.refresh === 'true'} onChange={e => updateMaterializeMetadata('refresh', e.target.checked)} />
              </>
            }
              <Button variant='outline' onClick={() => goToReportWriter('old')}>View in Report Writer</Button>
              <Button variant='outline' onClick={() => goToReportWriter('new')}>View in Report Writer New</Button>
            </Group>
          </Group>
        </Tabs.Panel>
        {report.query &&
          <Tabs.Panel value="query" p="xl">
            <StoredQuery query={report.query} state={state} lastModified={report.query_last_modified_time} dispatch={dispatch} />
          </Tabs.Panel>}
        <Tabs.Panel value='category' p='xl'>
          <ReportCategory categoryPk={report.category_pk} state={state} dispatch={dispatch} />
        </Tabs.Panel>
        <Tabs.Panel value='reportFields' p='xl'>
          <ReportFields report={report} state={state} dispatch={dispatch} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
};